import React, { useCallback } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import toast from 'react-hot-toast'
import AnswerLayout, { AnswerMain, AnswerSidebar } from './AnswerLayout'
import SwitchInput from 'Design/components/SwitchInput'

import AnswerForm from './AnswerForm'
import answerFormValidationSchema from './AnswerForm/answer-form-validation-schema'
import { useCreateAnswerMutation } from './answers.service'
import parseErrorMessage from 'Utils/error-message-parser'

import Tags from '../../../../components/Tags'

const TagsInput = ({ field: { value, onChange } }) => {
  const handleTagCreate = (tag) => {
    onChange([...value, tag])
  }

  const handleTagRemove = (tag) => {
    const newTags = value.filter((t) => t !== tag.value)
    onChange(newTags)
  }

  return <Tags value={value} creatable onCreate={handleTagCreate} onRemove={handleTagRemove} />
}

const CreateAnswer = () => {
  const navigate = useNavigate()

  const formMethods = useForm({
    defaultValues: {
      name: '',
      content: '',
      isActive: true,
      tags: []
    },
    resolver: yupResolver(answerFormValidationSchema)
  })

  const [createAnswer, answerResult] = useCreateAnswerMutation()

  const isLoading = answerResult.isLoading

  const onSubmit = useCallback(
    async (data, navigateToEdit) => {
      const payload = {
        name: data.name,
        content: data.content,
        is_active: data.isActive,
        tags: data.tags.map((tag) => tag.value)
      }

      try {
        const promise = createAnswer(payload).unwrap()

        toast.promise(promise, {
          loading: 'Creating answer',
          success: 'Answer created successfully',
          error: parseErrorMessage('Unable to create answer. Try again...')
        })

        if (navigateToEdit) {
          const answer = await promise
          navigate('../' + answer.id, { replace: true })
        }
      } catch {}
    },
    [createAnswer, navigate]
  )

  const handleSaveandAddNewAnswerClick = useCallback(
    (data) => {
      const goToEditAnswer = false
      onSubmit(data, goToEditAnswer)
      formMethods.reset()
    },
    [formMethods, onSubmit]
  )

  return (
    <AnswerLayout>
      <AnswerMain>
        <FormProvider {...formMethods}>
          <AnswerForm
            onSubmit={onSubmit}
            isLoading={isLoading}
            onSaveandAddNewAnswer={handleSaveandAddNewAnswerClick}
          />
        </FormProvider>
      </AnswerMain>
      <AnswerSidebar>
        <Controller
          control={formMethods.control}
          name='isActive'
          render={({ field: { value, onChange } }) => {
            return (
              <SwitchInput checked={value} onChange={onChange}>
                Published
              </SwitchInput>
            )
          }}
        />
        <Controller control={formMethods.control} name='tags' render={TagsInput} />
      </AnswerSidebar>
    </AnswerLayout>
  )
}

export default CreateAnswer
